import { cs } from '../../../lib/currency';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Banknote, Send, CreditCard, Loader2, ExternalLink, AlertTriangle, CheckCircle } from 'lucide-react';
import { useEffect } from 'react';

const fmt = (n) => `${cs()}${Number(n || 0).toFixed(2)}`;

/**
 * Unified Payment Actions dialog — three modes:
 *   - record:       manual payment recording (cash/check/zelle/venmo/etc)
 *   - send_link:    generate a Stripe Checkout URL and email it
 *   - process_card: in-person card via Stripe Checkout redirect
 */
export const PaymentActionsModal = ({
  open,
  onOpenChange,
  action,
  booking,
  form,
  setForm,
  processing,
  onRecord,
  onSendLink,
  onProcessCard,
}) => {
  // PR 19y5 — Overpayment detection (offline methods only). Mirrors the
  // Invoice MarkPaidDialog logic: if admin entered MORE than
  // booking.price, cap the recorded amount at booking.price and let
  // them pick "Credit the overage" or "Ignore" (physically returned).
  const bookingAmountDue = Number(booking?.price || 0);
  const enteredAmount = parseFloat(form?.amount) || 0;
  const isOfflineMethod = action === 'record' &&
    !['card', 'account_credit'].includes(form?.payment_method);
  const overage = isOfflineMethod && enteredAmount > bookingAmountDue + 0.005
    ? +(enteredAmount - bookingAmountDue).toFixed(2)
    : 0;
  const overageAction = form?.overage_action || 'credit';

  useEffect(() => {
    // Reset choice when overage clears.
    if (!overage && form?.overage_action) {
      setForm({ ...form, overage_action: 'credit' });
    }
  }, [overage]);

  return (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="font-heading text-xl">
          {action === 'record' && (<><Banknote className="h-5 w-5 inline mr-2 text-green-600" />Record Payment</>)}
          {action === 'send_link' && (<><Send className="h-5 w-5 inline mr-2 text-blue-600" />Send Payment Link</>)}
          {action === 'process_card' && (<><CreditCard className="h-5 w-5 inline mr-2 text-purple-600" />Process Card Payment</>)}
        </DialogTitle>
        <DialogDescription>
          {booking && (
            <span className="text-sm">
              Booking: {booking.customer_name} - {cs()}{booking.price?.toFixed(2)}
            </span>
          )}
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4 py-4">
        {action === 'record' && (
          <>
            <div className="space-y-2">
              <Label>Payment Method *</Label>
              <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                <SelectTrigger data-testid="payment-method-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="check">Check</SelectItem>
                  <SelectItem value="ach">ACH / Bank Transfer</SelectItem>
                  <SelectItem value="zelle">Zelle</SelectItem>
                  <SelectItem value="venmo">Venmo</SelectItem>
                  <SelectItem value="card">Card (in-person)</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Amount (leave blank for full amount)</Label>
              <Input
                type="number"
                step="0.01"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                placeholder={`${cs()}${booking?.price?.toFixed(2) || '0.00'}`}
                data-testid="payment-amount-input"
              />
            </div>
            {['check', 'zelle', 'venmo'].includes(form.payment_method) && (
              <div className="space-y-2">
                <Label>Reference / Transaction ID</Label>
                <Input
                  value={form.reference_number}
                  onChange={(e) => setForm({ ...form, reference_number: e.target.value })}
                  placeholder={form.payment_method === 'check' ? 'Check number' : 'Transaction ID'}
                  data-testid="payment-reference-input"
                />
              </div>
            )}

            {overage > 0 && (
              <div
                className="rounded-md border bg-amber-50/60 border-amber-300 p-3 text-sm space-y-2"
                data-testid="lane-payment-overage-alert"
              >
                <div className="flex items-start gap-1.5 text-amber-900">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <div>
                    <div className="font-semibold">Overpayment detected</div>
                    <div className="text-xs">
                      You entered <strong>{fmt(enteredAmount)}</strong> but the booking is
                      only <strong>{fmt(bookingAmountDue)}</strong> &mdash; that&apos;s{' '}
                      <strong>{fmt(overage)}</strong> extra.
                    </div>
                  </div>
                </div>
                <div className="space-y-1.5 pl-6">
                  <label className="flex items-start gap-2 text-xs cursor-pointer">
                    <input
                      type="radio"
                      name="lane_overage_action"
                      value="credit"
                      checked={overageAction === 'credit'}
                      onChange={() => setForm({ ...form, overage_action: 'credit' })}
                      className="mt-0.5"
                      data-testid="lane-payment-overage-credit"
                    />
                    <span>
                      <strong>Deposit {fmt(overage)} to customer&apos;s account credit</strong>
                      <span className="block text-slate-600">
                        Booking paid at {fmt(bookingAmountDue)}; overage recorded as a
                        credit ledger entry for future use.
                      </span>
                    </span>
                  </label>
                  <label className="flex items-start gap-2 text-xs cursor-pointer">
                    <input
                      type="radio"
                      name="lane_overage_action"
                      value="ignore"
                      checked={overageAction === 'ignore'}
                      onChange={() => setForm({ ...form, overage_action: 'ignore' })}
                      className="mt-0.5"
                      data-testid="lane-payment-overage-ignore"
                    />
                    <span>
                      <strong>Only record {fmt(bookingAmountDue)} (booking price)</strong>
                      <span className="block text-slate-600">
                        {fmt(overage)} not recorded anywhere — you handed the extra back
                        to the customer physically.
                      </span>
                    </span>
                  </label>
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Any additional notes..."
                rows={2}
                data-testid="payment-notes-input"
              />
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-700">
              <strong>Note:</strong> A paid invoice will be automatically emailed to the customer after recording the payment.
            </div>
          </>
        )}

        {action === 'send_link' && (
          <>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-700 mb-2">A secure Stripe payment link will be generated and emailed to:</p>
              <p className="font-medium text-blue-800">{booking?.customer_email || 'Customer email'}</p>
            </div>

            {/* Optional admin price override — same shape as the walk-in
                booking modal. Backend applies the change to the booking
                record FIRST, then generates the Stripe link reflecting
                the new total. Original price is preserved + audit-logged. */}
            <details className="rounded-md border border-slate-200 bg-slate-50/60 p-3" data-testid="send-link-price-override-block">
              <summary className="text-sm font-medium text-slate-700 cursor-pointer select-none">
                Adjust price before sending (optional)
              </summary>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-3">
                <div>
                  <Label className="text-xs">Override $</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder={`${booking?.price?.toFixed(2) || '0.00'}`}
                    value={form.price_override ?? ''}
                    onChange={(e) =>
                      setForm({ ...form, price_override: e.target.value === '' ? null : Number(e.target.value) })
                    }
                    className="h-9"
                    data-testid="send-link-price-override"
                  />
                </div>
                <div>
                  <Label className="text-xs">Discount %</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    value={form.discount_percent ?? ''}
                    onChange={(e) =>
                      setForm({ ...form, discount_percent: e.target.value === '' ? null : Number(e.target.value) })
                    }
                    className="h-9"
                    data-testid="send-link-discount-percent"
                  />
                </div>
                <div>
                  <Label className="text-xs">Discount $</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.discount_amount ?? ''}
                    onChange={(e) =>
                      setForm({ ...form, discount_amount: e.target.value === '' ? null : Number(e.target.value) })
                    }
                    className="h-9"
                    data-testid="send-link-discount-amount"
                  />
                </div>
                <div className="sm:col-span-3">
                  <Label className="text-xs">Reason (audit log)</Label>
                  <Input
                    value={form.price_adjustment_reason || ''}
                    onChange={(e) => setForm({ ...form, price_adjustment_reason: e.target.value })}
                    placeholder="Why the price changed (shows in admin ledger)"
                    className="h-9"
                    data-testid="send-link-price-reason"
                  />
                </div>
              </div>
              <p className="text-[11px] text-slate-500 mt-2">
                Override wins over discounts. Leave empty to send the current price.
              </p>
            </details>

            <div className="space-y-2">
              <Label>Custom Message (optional)</Label>
              <Textarea
                value={form.custom_message}
                onChange={(e) => setForm({ ...form, custom_message: e.target.value })}
                placeholder="Add a personal message to the payment email..."
                rows={3}
                data-testid="custom-message-input"
              />
            </div>
          </>
        )}

        {action === 'process_card' && (
          <div className="space-y-4">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <p className="text-sm text-purple-700 mb-2">You will be redirected to Stripe Checkout to process the card payment.</p>
              <p className="text-sm text-purple-600">Amount: <strong>{cs()}{booking?.price?.toFixed(2)}</strong></p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700">
              <strong>Tip:</strong> Use this for in-person card payments. Hand your device to the customer or use a card reader.
            </div>
          </div>
        )}
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)} disabled={processing}>Cancel</Button>
        {action === 'record' && (
          <Button onClick={onRecord} disabled={processing} className="bg-green-600 hover:bg-green-700" data-testid="confirm-record-payment">
            {processing ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Recording...</>
            ) : overage > 0 && overageAction === 'credit' ? (
              <><CheckCircle className="h-4 w-4 mr-2" />
                Record {fmt(bookingAmountDue)} + {fmt(overage)} credit
              </>
            ) : overage > 0 && overageAction === 'ignore' ? (
              <><CheckCircle className="h-4 w-4 mr-2" />
                Record {fmt(bookingAmountDue)}
              </>
            ) : (
              <><Banknote className="h-4 w-4 mr-2" />Record Payment</>
            )}
          </Button>
        )}
        {action === 'send_link' && (
          <Button onClick={onSendLink} disabled={processing} className="bg-blue-600 hover:bg-blue-700" data-testid="confirm-send-link">
            {processing ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending...</>) : (<><Send className="h-4 w-4 mr-2" />Send Link</>)}
          </Button>
        )}
        {action === 'process_card' && (
          <Button onClick={onProcessCard} disabled={processing} className="bg-purple-600 hover:bg-purple-700" data-testid="confirm-process-card">
            {processing ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Processing...</>) : (<><ExternalLink className="h-4 w-4 mr-2" />Go to Checkout</>)}
          </Button>
        )}
      </DialogFooter>
    </DialogContent>
  </Dialog>
  );
};

export default PaymentActionsModal;
